import React, { useMemo } from 'react'
import SimpleCard from '../card'
import { NewsItemType } from 'app/modules/admin/features/settings/appearance/news/types'
import { nlToBr } from 'app/modules/shared/helpers'
import { useModal } from '../modal'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import Button from 'app/modules/shared/components/button'

type NewsItemProps = {
  newsItem: NewsItemType
  onEdit?: CallableFunction
  onRemove?: CallableFunction
}
export function NewsItem({ newsItem, onEdit, onRemove }: NewsItemProps) {
  const formattedText = useMemo(() => formatText(newsItem.text), [newsItem.text])
  const imgLinks = useMemo(() => formatImages(newsItem.images), [newsItem.images])
  const imgColClass = imgLinks.length === 1 ? '' : 'col-6'
  const editorMode = typeof onEdit !== 'undefined'
  const className = 'news-container ' + (editorMode ? 'editor-mode ' : '') + (!newsItem.active ? 'news-inactive' : '')

  return (
    <div className="col-sm-6 col-md-4">
      <SimpleCard className="h-100 card-hover news-card">
        <div className={className}>
          {editorMode ? (
            <EditorControls newsItem={newsItem} onEdit={onEdit} onRemove={onRemove as CallableFunction} />
          ) : null}

          <a
            className="static-news"
            href={editorMode ? undefined : newsItem.link}
            target="_blank"
            rel="noreferrer nofollow"
            onClick={(e) => editorMode && e.preventDefault()}
          >
            <div className="news-header ellipsis">
              <div className="news-header-image" />
              <div className="news-header-text">
                <div className="small-header me-1 mb-0">Filescan.io</div>
                <div className="text-note text-muted">@filescan_itsec&#183;{newsItem.date}</div>
              </div>
            </div>

            <div className="news-body">
              <div className="news-text mt-4 mb-4 overflow-hidden">{formattedText}</div>

              <div className="news-images row">
                {imgLinks.map((imgLink) => (
                  <NewsImage key={imgLink} imgLink={imgLink} imgColClass={imgColClass} />
                ))}
              </div>
            </div>
          </a>
        </div>
      </SimpleCard>
    </div>
  )
}

function NewsImage({ imgLink, imgColClass }: Readonly<{ imgLink: string; imgColClass: string }>) {
  const [showModal, hideModal] = useModal()
  const { _t } = useTypedTranslation()

  const previewImage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    showModal({
      body: <img src={imgLink} alt="News" className="w-100 h-100" style={{ maxHeight: 'calc(100vh - 180px)' }} />,
      title: <div className="h3 mb-0">{_t('image-preview')}</div>,
      props: {
        dialogClassName: 'modal-size-auto',
        onHide: hideModal,
      },
    })
  }

  return (
    <Button
      id="news-image-preview"
      tooltip={_t('click-to-preview-image')}
      tooltipPlacement="auto"
      variant="plain"
      className={imgColClass}
      onClick={previewImage}
    >
      <img src={imgLink} alt="News" className="w-100 h-100" />
    </Button>
  )
}

type EditorControlsProps = {
  newsItem: NewsItemType
  onEdit: CallableFunction
  onRemove: CallableFunction
}
function EditorControls({ newsItem, onEdit, onRemove }: EditorControlsProps) {
  return (
    <div className="editor-controls gap-2">
      <a href={newsItem.link} target="_blank" rel="noreferrer nofollow">
        <i className="ds3-icon ds3-external-link color-text-main ds3-hover"></i>
      </a>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
          onEdit()
        }}
      >
        <i className="ds3-icon ds3-edit color-text-main ds3-hover"></i>
      </a>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
          onRemove()
        }}
      >
        <i className="ds3-icon ds3-remove color-text-main ds3-hover"></i>
      </a>
    </div>
  )
}

function formatText(text: string) {
  const parts = text.replace(/\[\/link\]/g, '[link]').split('[link]')

  let isLink = false
  const formattedParts = parts.map((part, idx) => {
    isLink = !isLink && !!part.length && idx > 0

    return isLink ? (
      <div className="news-content-link" key={part}>
        {part}
      </div>
    ) : (
      nlToBr(part)
    )
  })

  return formattedParts
}

function formatImages(images: string) {
  if (!images) {
    return []
  }

  return images
    .split(',')
    .map((link) => link.replace(/&amp;/g, '&').trim())
    .filter((link) => link.length > 0)
}
